import playbackHelpers from './playbackRejectionShouldFallback.util.js';

const {
  playbackRejectionShouldFallback,
  playbackAbortShouldRetry,
  shouldAttemptPlayback,
  shouldPauseForVisibility,
} = playbackHelpers;

const {registerModule} = window;
const HAVE_FUTURE_DATA = 3;
const IN_VIEW_THRESHOLD_DEFAULT = 0.5;

/**
 * Plays a muted inline hero or feature video and, when autoplay is blocked
 * (Safari Low Power Mode), hides the video so the still image shows.
 * play() runs only for a visible paused video, so off-screen videos stay
 * under the browser's own pause. A video this script starts is paused again
 * once it leaves the viewport.
 */
class AutoplayVideo {
  constructor(element) {
    this.element = element;
    this.initialize();
  }

  initialize() {
    this.bindUI();
    if (!this.ui.$video) {
      return;
    }
    this.setVars();
    this.watchVisibility();
  }

  bindUI() {
    this.ui = {};
    this.ui.$video = this.element.querySelector('video:not([data-ref="autoplay-video-fallback"])');
    this.ui.$fallbacks = this.element.querySelectorAll('[data-ref="autoplay-video-fallback"]');
    this.primeFallbackImages();
  }

  fallbackImages() {
    const images = [];
    this.ui.$fallbacks.forEach((node) => {
      if (node.tagName === 'IMG') {
        images.push(node);
        return;
      }
      node.querySelectorAll('img').forEach((img) => {
        images.push(img);
      });
    });
    return images;
  }

  // A hidden lazy image is not fetched. Eager starts the request while the
  // picture is still hidden, so the still is ready when playback is blocked.
  primeFallbackImages() {
    this.fallbackImages().forEach((img) => {
      img.loading = 'eager';
    });
  }

  setVars() {
    const thresholdAttr = this.ui.$video.getAttribute('data-autoplay-after-percent-of-video-visible');
    this.usesThreshold = thresholdAttr !== null && thresholdAttr !== '';
    this.inViewThreshold = parseFloat(thresholdAttr) / 100 || IN_VIEW_THRESHOLD_DEFAULT;
    this.hasRetriedPlayback = false;
    this.startedByScript = false;
    this.didShowFallback = false;
    this.visibilityPause = false;
    this.playPending = false;
    this.isOnScreen = false;
    this.isReadyToPlay = false;
  }

  watchVisibility() {
    if (this.usesThreshold && (this.inViewThreshold < 0 || this.inViewThreshold > 1)) {
      return;
    }
    this.startThreshold = this.usesThreshold ? this.inViewThreshold : 0;

    if (typeof IntersectionObserver !== 'function') {
      if (!this.usesThreshold) {
        this.isOnScreen = true;
        this.isReadyToPlay = true;
        this.attemptPlayback();
      }
      return;
    }

    const thresholds = this.startThreshold > 0 ? [0, this.startThreshold] : [0];
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        this.onVisibility(entry);
      });
    }, {threshold: thresholds});
    this.observer.observe(this.ui.$video);
  }

  onVisibility(entry) {
    this.isOnScreen = entry.isIntersecting;
    this.isReadyToPlay = this.usesThreshold ?
      entry.isIntersecting && entry.intersectionRatio >= this.startThreshold :
      entry.isIntersecting;

    if (shouldPauseForVisibility({
      startedByScript: this.startedByScript,
      fallbackShown: this.didShowFallback,
      visible: this.isOnScreen,
      paused: this.ui.$video.paused,
      playPending: this.playPending,
    })) {
      this.pauseForVisibility();
    }

    this.attemptPlayback();
  }

  pauseForVisibility() {
    this.visibilityPause = true;
    this.ui.$video.pause();
    window.setTimeout(() => {
      this.visibilityPause = false;
    }, 0);
  }

  attemptPlayback() {
    if (this.playPending || this.didShowFallback) {
      return;
    }
    if (!shouldAttemptPlayback({
      paused: this.ui.$video.paused,
      visible: this.isReadyToPlay,
      fallbackShown: this.didShowFallback,
    })) {
      return;
    }

    const video = this.ui.$video;
    this.playPending = true;
    this.startedByScript = true;
    let pending;
    try {
      pending = video.play();
    } catch (error) {
      this.playPending = false;
      if (playbackRejectionShouldFallback(error)) {
        this.showFallback();
      }
      return;
    }
    if (!pending || typeof pending.then !== 'function') {
      this.playPending = false;
      if (video.paused) {
        this.showFallback();
      } else {
        this.startedByScript = true;
      }
      return;
    }

    pending.then(() => {
      this.playPending = false;
      if (video.paused) {
        this.showFallback();
        return;
      }
      if (!this.isOnScreen) {
        this.pauseForVisibility();
      }
    }).catch((error) => {
      this.playPending = false;
      if (playbackAbortShouldRetry(error, {
        visibilityPause: this.visibilityPause,
        onScreen: this.isOnScreen,
      })) {
        this.retryPlayback();
        return;
      }
      if (!playbackRejectionShouldFallback(error) || this.visibilityPause) {
        return;
      }
      this.showFallback();
    });
  }

  retryPlayback() {
    if (this.hasRetriedPlayback) {
      this.showFallback();
      return;
    }
    this.hasRetriedPlayback = true;

    const video = this.ui.$video;
    if (video.readyState >= HAVE_FUTURE_DATA) {
      window.setTimeout(() => {
        this.attemptPlayback();
      }, 0);
      return;
    }
    video.addEventListener('canplay', () => {
      this.attemptPlayback();
    }, {once: true});
  }

  showFallback() {
    if (this.didShowFallback) {
      return;
    }
    this.didShowFallback = true;
    if (this.observer) {
      this.observer.disconnect();
    }
    if (!this.ui.$fallbacks.length) {
      return;
    }
    this.whenFallbackReady(() => {
      this.revealFallback();
    });
  }

  imageSettled(img) {
    return img.complete && (img.naturalWidth > 0 || Boolean(img.currentSrc));
  }

  whenFallbackReady(callback) {
    const images = this.fallbackImages().filter((img) => !this.imageSettled(img));
    if (!images.length) {
      callback();
      return;
    }
    let remaining = images.length;
    const done = () => {
      remaining -= 1;
      if (remaining === 0) {
        callback();
      }
    };
    images.forEach((img) => {
      img.loading = 'eager';
      img.addEventListener('load', done, {once: true});
      img.addEventListener('error', done, {once: true});
    });
  }

  revealFallback() {
    this.ui.$video.hidden = true;
    this.ui.$fallbacks.forEach((node) => {
      node.hidden = false;
      if (node.tagName !== 'VIDEO') {
        return;
      }
      const pending = node.play();
      if (pending && typeof pending.catch === 'function') {
        pending.catch(() => {});
      }
    });
  }
}

registerModule('AutoplayVideo', AutoplayVideo);
